import { useMemo, useCallback } from 'react';
import debounce from 'lodash/debounce';

import requestUtilityApi from 'client/actions/requestUtilityApi';
import { ADDRESS_PREDICTION_PROVIDER_SMARTY } from 'common/utility/constants';
import { SR_API_ACTION_ADDRESS_GET_AUTOCOMPLETE_PREDICTIONS } from 'api/utilityActionTypes';

const useSmartyAddressSearchProvider = ({ dispatch, formFieldId }) => {
  const fetchPredictions = useCallback(
    (searchText, selected = null) => {
      const parsedSearchText = searchText.length >= 32 ? searchText.slice(0, 31) : searchText;
      return dispatch(requestUtilityApi, {
        queryId: `${formFieldId}_${searchText}`,
        action: SR_API_ACTION_ADDRESS_GET_AUTOCOMPLETE_PREDICTIONS,
        payload: {
          search: parsedSearchText,
          ...(selected && { selected }),
          addressSearchProvider: ADDRESS_PREDICTION_PROVIDER_SMARTY,
        },
      }).then((response) => response?.predictions || []);
    },
    [dispatch, formFieldId],
  );

  const debouncedFetchPredictions = useMemo(
    () =>
      debounce((searchText, selected, requestId, onResults) => {
        fetchPredictions(searchText, selected)
          .then((results) => onResults(results, requestId))
          .catch(() => onResults([], requestId));
      }, 300),
    [fetchPredictions],
  );

  const cancelDebounced = useCallback(() => {
    debouncedFetchPredictions.cancel();
  }, [debouncedFetchPredictions]);

  const buildSecondarySearchQuery = useCallback((suggestion) => {
    const { streetLine, secondary, entriesCount, city, state, zipcode } = suggestion;
    const searchString = `${streetLine} ${secondary}`;
    return {
      search: searchString,
      selected: `${searchString} (${entriesCount}) ${city} ${state} ${zipcode}`,
    };
  }, []);

  return {
    fetchPredictions,
    debouncedFetchPredictions,
    cancelDebounced,
    buildSecondarySearchQuery,
  };
};

export default useSmartyAddressSearchProvider;
